import React, { useState } from 'react';
import {
    Table,
    Card,
    Row,
    Col,
    Input,
    Button,
    Space,
    Tag,
    Typography,
    Tooltip,
    Divider,
    message,
    Modal,
    Form,
    Select,
    Drawer,
    Upload,
    Radio,
    Checkbox,
    Descriptions,
    Badge,
    Tabs,
    Image
} from 'antd';
import {
    PlusOutlined,
    InfoCircleOutlined,
    ExclamationCircleFilled,
    CloudServerOutlined,
    GlobalOutlined,
    AppleOutlined,
    AndroidOutlined,
    EyeOutlined,
    DownloadOutlined,
    EditOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;
const { Option } = Select;

const MOCK_LANGS = [
    { label: '英语 (美国)', key: 'en-US' },
    { label: '日语 (日本)', key: 'ja-JP' },
    { label: '德语 (德国)', key: 'de-DE' },
    { label: '法语 (法国)', key: 'fr-FR' },
];

const AppManage = () => {
    const [loading] = useState(false);
    const [searchForm] = Form.useForm();
    const [addForm] = Form.useForm();
    const [showAddDrawer, setShowAddDrawer] = useState(false);
    const [showAddVersionDrawer, setShowAddVersionDrawer] = useState(false);
    const [showVersionDetailDrawer, setShowVersionDetailDrawer] = useState(false);
    const [currentApp, setCurrentApp] = useState(null);
    const [currentVersion, setCurrentVersion] = useState(null);
    const [versionForm] = Form.useForm();
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [downloadVisible, setDownloadVisible] = useState(false);
    const [downloadApp, setDownloadApp] = useState(null);
    const [downloadForm] = Form.useForm();
    const [i18nModal, setI18nModal] = useState({ open: false, title: '', originValue: '' });
    const [editAppVisible, setEditAppVisible] = useState(false);
    const [, setEditAppRecord] = useState(null);
    const [editAppForm] = Form.useForm();
    const [configPayVisible, setConfigPayVisible] = useState(false);
    const [configPayRecord, setConfigPayRecord] = useState(null);
    const [configPayForm] = Form.useForm();
    const [regionModalOpen, setRegionModalOpen] = useState(false);
    const [editingRegion, setEditingRegion] = useState(null);
            const [regionOverrides, setRegionOverrides] = useState([]);
    const [regionFormMode, setRegionFormMode] = useState('custom');
    const [regionFormChannels, setRegionFormChannels] = useState(['Google Pay']);
    const [regionFormCountry, setRegionFormCountry] = useState('日本（JP）');
    // 获取默认支付已启用的渠道（企业自收款）
    const allChannels = ['Google Pay', 'Apple Pay', 'PayPal', '微信支付', '支付宝'];

    // 监听表单值变化用于动态渲染
    const selectedPlatform = Form.useWatch('platform', versionForm);
    const selectedRegion = Form.useWatch('region', versionForm);

    // 渲染动态提示信息
    const renderRegionTip = () => {
        if (!selectedRegion) return null;
        
        const config = {
            mainland: {
                tip: '如果公司业务都是在中国大陆地区，推荐选择此方案。',
                ios: '中国大陆版',
                android: '中国大陆版'
            },
            overseas: {
                tip: '如果公司业务都是在非中国大陆地区，推荐选择此方案。',
                ios: 'Apple国际版',
                android: '谷歌国际版 华为国际版'
            },
            global: {
                tip: '如果公司业务都是在非中国大陆地区，推荐选择此方案。',
                ios: '全球通用版',
                android: '中国大陆版 境外谷歌版 境外华为版'
            }
        };

        const current = config[selectedRegion];
        return (
            <div style={{ backgroundColor: '#f5f5f5', padding: '16px 24px', borderRadius: '4px', marginBottom: 24 }}>
                <div style={{ color: '#ff4d4f', marginBottom: 12 }}>{current.tip}</div>
                <div style={{ color: '#595959', fontSize: 13 }}>
                    <div>针对移动端提供安装包：</div>
                    <div style={{ marginTop: 12, paddingLeft: 24 }}>
                        <div style={{ marginBottom: 8 }}>iOS安装包：{current.ios}</div>
                        <div>Android安装包：{current.android}</div>
                    </div>
                </div>
            </div>
        );
    };

    // 渲染平台特定的输入框
    const renderPlatformInput = () => {
        if (!selectedPlatform) return null;

        const platformConfig = {
            Android: { label: 'Android应用包名(AppID)', placeholder: '格式: com.xxx.xxx', tooltip: '上架到应用市场的AppID，仅支持输入字母、数字、下划线。用"."进行分段，至少2段，每段以字母开头。' },
            iOS: { label: 'iOS Bundle ID(AppID)', placeholder: '格式: com.xxx.xxx', tooltip: '上架到应用市场的AppID，仅支持输入字母、数字、下划线。用"."进行分段，至少2段，每段以字母开头。' },
            '微信小程序': { label: '小程序AppID(AppID)', placeholder: '格式: wx.xxxxxxxxxxxx' },
            windows: { label: '包名(AppID)', placeholder: '请输入', tooltip: '用于微软商店发布或 SDK 授权，可不填。' },
            macOS: { label: 'iOS Bundle ID(AppID)', placeholder: '格式: com.xxx.xxx', tooltip: '上架到应用市场的AppID，仅支持输入字母、数字、下划线。用"."进行分段，至少2段，每段以字母开头。' },
            Linux: { label: 'ID(AppID)', placeholder: '请输入' },
            'Web/H5': { label: '域名(AppID)', placeholder: '格式: https://xxxxxx' },
        };

        const config = platformConfig[selectedPlatform];
        if (!config) return null;

        return (
            <Form.Item 
                label={
                    <Space>
                        {config.label}
                        {config.tooltip && (
                            <Tooltip title={config.tooltip}>
                                <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                            </Tooltip>
                        )}
                    </Space>
                } 
                name="bundleId" 
                rules={[{ required: selectedPlatform !== 'windows', message: `请输入${config.label}` }]}
            >
                <Input placeholder={config.placeholder} />
            </Form.Item>
        );
    };

    // 模拟应用数据（父级）
    const dataSource = [
        {
            key: '1',
            appName: '鹤梦智能',
            payMethod: 'platform', // 平台代收款
            versionStats: { total: 4, pass: 2, pending: 1, reject: 1 },
            createTime: '2025-12-19 18:16:53',
            platforms: [
                { key: '1-1', platform: 'iOS', region: 'overseas', bundleId: 'com.hm.smart.ios', status: 'pass', createTime: '2025-12-20 10:00:00', payChannels: ['Google Pay', 'Apple Pay', '微信支付'], operationMode: 'platform' },
                { key: '1-2', platform: 'Android', region: 'mainland', bundleId: 'com.hm.smart.android', status: 'draft', createTime: '2025-12-21 11:30:00', payChannels: [], operationMode: 'self' },
                { key: '1-4', platform: 'windows', region: 'mainland', bundleId: 'com.hm.smart.win', status: 'reject', createTime: '2026-03-02 14:00:00', rejectReason: '应用包名(AppID)不规范，请修改后重新提交', operationMode: 'self' },
                { key: '1-3', platform: '微信小程序', region: 'mainland', bundleId: 'wx12345678', status: 'pending', createTime: '2026-03-01 09:20:00', operationMode: 'self' },
            ]
        },
        {
            key: '2',
            appName: '鹤梦云联',
            payMethod: 'enterprise', // 企业自收款
            versionStats: { total: 2, pass: 1, pending: 0, reject: 1 },
            createTime: '2025-12-17 22:35:26',
            platforms: [
                { key: '2-1', platform: 'iOS', region: 'global', bundleId: 'com.hm.cloud.ios', status: 'pass', createTime: '2025-12-18 14:20:00', payConfig: { 'Google Pay': { enabled: true, accountId: '22222' }, 'Apple Pay': { enabled: true, accountId: '11111' } }, operationMode: 'self' },
                { key: '2-2', platform: 'Android', region: 'global', bundleId: 'com.hm.cloud.android', status: 'reject', createTime: '2025-12-19 16:45:00', rejectReason: '包名重复', payConfig: { '微信支付': { enabled: true, accountId: '11111' }, '支付宝': { enabled: false } }, operationMode: 'self' },
            ]
        },
        {
            key: '3',
            appName: '智家生活',
            payMethod: 'platform',
            versionStats: { total: 4, pass: 3, pending: 1, reject: 0 },
            createTime: '2025-12-11 21:58:54',
            platforms: [
                { key: '3-1', platform: 'iOS', region: 'mainland', bundleId: 'com.hm.life.ios', status: 'pass', createTime: '2025-12-12 09:00:00' },
                { key: '3-2', platform: 'Android', region: 'mainland', bundleId: 'com.hm.life.android', status: 'pass', createTime: '2025-12-13 10:30:00' },
                { key: '3-3', platform: 'windows', region: 'mainland', bundleId: 'hm-life-win', status: 'pass', createTime: '2025-12-14 15:00:00' },
                { key: '3-4', platform: 'macOS', region: 'mainland', bundleId: 'com.hm.life.mac', status: 'pending', createTime: '2026-03-05 14:00:00' },
            ]
        },
        {
            key: '4',
            appName: '工业物联网平台',
            payMethod: 'enterprise',
            versionStats: { total: 1, pass: 1, pending: 0, reject: 0 },
            createTime: '2025-11-20 09:15:22',
            platforms: [
                { key: '4-1', platform: 'Web/H5', region: 'mainland', bundleId: 'https://iot.hm.com', status: 'pass', createTime: '2025-11-21 10:00:00' },
            ]
        },
        {
            key: '5',
            appName: '鹤梦开发者助手',
            payMethod: 'platform',
            versionStats: { total: 2, pass: 0, pending: 2, reject: 0 },
            createTime: '2026-02-28 16:40:10',
            platforms: [
                { key: '5-1', platform: 'Android', region: 'overseas', bundleId: 'com.hm.dev.helper', status: 'pending', createTime: '2026-03-01 10:00:00' },
                { key: '5-2', platform: 'iOS', region: 'overseas', bundleId: 'com.hm.dev.helper.ios', status: 'pending', createTime: '2026-03-02 11:00:00' },
            ]
        }
    ];

    const columns = [
        {
            title: '应用基本信息',
            dataIndex: 'appName',
            key: 'appName',
            render: (text, record) => (
                <Space size={12}>
                    <CloudServerOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                    <div>
                        <div style={{ fontWeight: 600 }}>{text} <EditOutlined style={{ fontSize: 12, color: '#1890ff', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setEditAppRecord(record); editAppForm.setFieldsValue({ appName: record.appName }); setEditAppVisible(true); }} /></div>
                    </div>
                </Space>
            )
        },
        {
            title: '收款方式',
            dataIndex: 'payMethod',
            key: 'payMethod',
            render: (type) => {
                const config = {
                    platform: '平台代收款',
                    enterprise: '企业自收款',
                };
                return config[type] || '—';
            },
        },
        {
            title: '覆盖平台',
            dataIndex: 'versionStats',
            key: 'systemPlatform',
            render: (stats) => (
                <Space>
                    <Tag color="processing">{stats.total} 个端</Tag>
                    {stats.pass > 0 && <Tag color="success">已生效 {stats.pass}</Tag>}
                </Space>
            ),
        },
        {
            title: '运营标签',
            key: 'operationTags',
            render: (_, record) => (
                <Space size={4} wrap>
                    {record.platforms.map(p => {
                        const mode = p.operationMode || 'self';
                        return (
                            <Tag key={p.key} color={mode === 'platform' ? 'blue' : 'default'} style={{ border: 'none', margin: 0 }}>
                                {p.platform} · {mode === 'platform' ? '平台代运营' : '企业自运营'}
                            </Tag>
                        );
                    })}
                </Space>
            )
        },
        {
            title: '创建时间',
            dataIndex: 'createTime',
            key: 'createTime',
            width: 180,
            sorter: (a, b) => new Date(a.createTime) - new Date(b.createTime),
        },
        {
            title: '操作',
            key: 'action',
            width: 260,
            align: 'center',
            render: (_, record) => (
                <Space size={0} split={<Divider type="vertical" style={{ margin: '0 2px' }} />}>
                    <Button 
                        type="link" 
                        size="small" 
                                                onClick={(e) => {
                            e.stopPropagation();
                            setCurrentApp(record);
                            setShowAddVersionDrawer(true);
                        }}
                    >
                        添加平台
                    </Button>
                    <Button 
                        type="link" 
                        size="small" 
                                                onClick={(e) => {
                            e.stopPropagation();
                            setDownloadApp(record);
                            downloadForm.setFieldsValue({
                                defaultUrl: record.downloadConfig?.defaultUrl || '',
                                brands: record.downloadConfig?.brands || {},
                            });
                            setDownloadVisible(true);
                        }}
                    >
                        下载配置
                    </Button>
                    {record.versionStats.pass === 0 && (
                    <Button 
                        type="link" 
                        size="small" 
                        danger
                                                onClick={(e) => {
                            e.stopPropagation();
                            message.warning('删除应用逻辑暂未实现');
                        }}
                    >
                        删除
                    </Button>
                    )}
                </Space>
            ),
        },
    ];

    const handleDeleteVersion = (record) => {
        Modal.confirm({
            title: '删除平台确认',
            icon: <ExclamationCircleFilled />,
            content: (
                <div>
                    你正在删除<Text strong>【{record.platform}】</Text>平台，删除后将不可恢复，确认继续删除操作吗？
                </div>
            ),
            okText: '确认',
            okButtonProps: { danger: true },
            cancelText: '取消',
            onOk() {
                message.success('平台删除成功');
            },
        });
    };

    const handleAddVersion = () => {
        versionForm.validateFields().then(values => {
            message.success('创建平台申请已提交');
            setShowAddVersionDrawer(false);
            versionForm.resetFields();
        });
    };

    const handleSavePayConfig = async () => {
        try {
            await configPayForm.validateFields();
            message.success('支付配置已保存');
            setConfigPayVisible(false);
            configPayForm.resetFields();
        } catch (e) { /* validation error */ }
    };

    const expandedRowRender = (record) => {
        const subColumns = [
            { 
                title: '系统平台', 
                dataIndex: 'platform', 
                key: 'platform',
                render: (text) => {
                    const iconMap = {
                        iOS: <AppleOutlined />,
                        Android: <AndroidOutlined />,
                        '微信小程序': <GlobalOutlined />,
                        'Web/H5': <GlobalOutlined />,
                        'windows': <GlobalOutlined />,
                        'macOS': <AppleOutlined />
                    };
                    return (
                        <Space>
                            {iconMap[text] || <GlobalOutlined />}
                            {text}
                        </Space>
                    );
                }
            },
            { 
                title: '上架区域', 
                dataIndex: 'region', 
                key: 'region',
                render: (text) => {
                    const map = { mainland: '国内', global: '全球', overseas: '非大陆' };
                    return map[text] || text;
                }
            },
            { title: 'AppID', dataIndex: 'bundleId', key: 'bundleId' },
            { 
                title: '状态', 
                dataIndex: 'status', 
                key: 'status',
                render: (status, item) => {
                    const config = {
                        pass: { color: 'green', text: '已生效' },
                        pending: { color: 'gold', text: '审核中' },
                        reject: { color: 'red', text: '已驳回' },
                        draft: { color: 'default', text: '待提交' },
                    };
                    const statusConfig = config[status] || { color: 'default', text: status };
                    return (
                        <Space direction="vertical" align="start" size={0}>
                            <Tag color={statusConfig.color}>{statusConfig.text}</Tag>
                            {status === 'reject' && (
                                <Tooltip title={item.rejectReason}>
                                    <div style={{ 
                                        color: '#ff4d4f', 
                                        fontSize: '11px', 
                                        marginTop: '4px',
                                        maxWidth: '120px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        cursor: 'help'
                                    }}>
                                        原因：{item.rejectReason}
                                    </div>
                                </Tooltip>
                            )}
                        </Space>
                    );
                }
            },
            { 
                title: '运营方式', 
                key: 'operationMode',
                width: 110,
                render: (_, item) => (
                    <Tag color={item.operationMode === 'platform' ? 'blue' : 'default'} style={{ border: 'none' }}>
                        {item.operationMode === 'platform' ? '平台代运营' : '企业自运营'}
                    </Tag>
                )
            },
            { title: '申请时间', dataIndex: 'createTime', key: 'createTime' },
            { 
                title: '操作', 
                key: 'action',
                width: 200,
                render: (_, item) => (
                    <Space size={0} split={<Divider type="vertical" style={{ margin: '0 2px' }} />}>
                        <Button type="link" size="small" onClick={() => {
                            setConfigPayRecord({ version: item, app: record });
                            if (record.payMethod === 'enterprise') {
                                configPayForm.setFieldsValue({
                                    payConfig: item.payConfig || {}
                                });
                            } else {
                                configPayForm.setFieldsValue({
                                    payChannels: item.payChannels || []
                                });
                            }
                            setConfigPayVisible(true);
                        }}>配置支付</Button>
                        {item.status === 'draft' && (
                            <Button type="link" size="small" onClick={() => message.success('已提交审核')}>提交审核</Button>
                        )}
                        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => {
                            setCurrentVersion(item);
                            setShowVersionDetailDrawer(true);
                        }}>详情</Button>
                        <Button 
                            type="link" 
                            size="small" 
                            danger 
                                                        disabled={item.status === 'pending'}
                            onClick={() => handleDeleteVersion(item)}
                        >
                            删除
                        </Button>
                    </Space>
                )
            },
        ];

        return (
            <Table 
                columns={subColumns} 
                dataSource={record.platforms} 
                pagination={false} 
                size="small"
                style={{ margin: '8px 0', border: '1px solid #f0f0f0', borderRadius: '4px' }}
            />
        );
    };

    return (
        <div style={{ padding: '24px', backgroundColor: '#fff', minHeight: '100vh' }}>
            <div style={{ marginBottom: 16 }}>
                <Title level={4}>我的App</Title>
            </div>
            <>
            {/* 搜索面板 */}
            <Card bordered={false} style={{ marginBottom: 16 }}>
                <Form form={searchForm} layout="horizontal">
                    <Row gutter={[24, 16]}>
                        <Col span={8}>
                            <Form.Item name="keyword" style={{ marginBottom: 0 }}>
                                <Select placeholder="请选择应用" allowClear showSearch optionFilterProp="label" options={dataSource.map(d => ({ value: d.appName, label: d.appName }))} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="AppID" name="bundleId" style={{ marginBottom: 0 }}>
                                <Input placeholder="请输入平台 AppID" allowClear style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Space size="middle">
                                <Button type="primary" onClick={() => message.info('搜索中...')}>查询</Button>
                                <Button onClick={() => searchForm.resetFields()}>重置</Button>
                            </Space>
                        </Col>
                    </Row>
                </Form>
            </Card>

            {/* 列表内容 */}
            <div style={{ marginBottom: 16 }}>
                <Button 
                    type="primary" 
                                        style={{ borderRadius: '4px', height: '36px' }}
                    onClick={() => setShowAddDrawer(true)}
                >
                    创建应用
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                rowKey="key"
                expandable={{
                    expandedRowRender,
                    onExpand: (expanded, record) => {
                        setExpandedRowKeys(expanded ? [record.key] : []);
                    },
                    expandedRowKeys: expandedRowKeys,
                }}
                onRow={(record) => ({
                    onClick: () => {
                        const isExpanded = expandedRowKeys.includes(record.key);
                        setExpandedRowKeys(isExpanded ? [] : [record.key]);
                    },
                    style: { cursor: 'pointer' }
                })}
                pagination={{
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `共 ${total} 条记录`,
                    defaultPageSize: 10,
                }}
                style={{ border: '1px solid #f0f0f0', borderRadius: '8px' }}
            />
            </>

            {/* 创建应用抽屉 */}
            <Drawer
                title={<Title level={4} style={{ margin: 0 }}>创建应用</Title>}
                width={560}
                onClose={() => {
                    setShowAddDrawer(false);
                    addForm.resetFields();
                }}
                open={showAddDrawer}
                bodyStyle={{ padding: '24px' }}
                footer={
                    <div style={{ textAlign: 'right', padding: '16px 24px' }}>
                        <Space size={12}>
                            <Button onClick={() => {
                                setShowAddDrawer(false);
                                addForm.resetFields();
                            }}>取消</Button>
                            <Button onClick={() => {
                                addForm.validateFields().then(() => {
                                    message.success('应用创建成功');
                                    setShowAddDrawer(false);
                                    addForm.resetFields();
                                });
                            }}>仅保存</Button>
                            <Button type="primary" onClick={() => {
                                addForm.validateFields().then(() => {
                                    message.success('基本信息已保存，请继续配置平台信息');
                                    setShowAddDrawer(false);
                                    addForm.resetFields();
                                    // 模拟当前选中的应用，以便添加平台
                                    setCurrentApp({ appName: addForm.getFieldValue('appName') || '新创建应用' });
                                    setShowAddVersionDrawer(true);
                                });
                            }}>保存并继续</Button>
                        </Space>
                    </div>
                }
            >
                <Form
                    form={addForm}
                    layout="vertical"
                    initialValues={{ payMethod: 'enterprise' }}
                >
                    <Form.Item label={
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <span><span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>应用名称</span>
                            <Button type="link" size="small" icon={<GlobalOutlined />}
                                onClick={() => setI18nModal({ open: true, title: '配置多语言翻译 - 应用名称', originValue: addForm.getFieldValue('appName') })}
                            >配置多语言 (已配置 0/{MOCK_LANGS.length})</Button>
                        </div>
                    } colon={false}>
                        <Form.Item name="appName" rules={[{ required: true, message: '请输入应用名称' }]} noStyle>
                            <Input placeholder="请输入应用名称（默认中文）" maxLength={40} showCount />
                        </Form.Item>
                    </Form.Item>

                    <Form.Item
                        label="应用LOGO"
                        name="appLogo"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}
                        extra="建议尺寸 200*200，支持 png、jpg 格式"
                    >
                        <Upload
                            listType="picture-card"
                            maxCount={1}
                            beforeUpload={() => false}
                        >
                            <div>
                                <PlusOutlined />
                                <div style={{ marginTop: 8 }}>上传图片</div>
                            </div>
                        </Upload>
                    </Form.Item>

                    <Form.Item label="收款方式" name="payMethod" required>
                        <Radio.Group style={{ width: '100%' }}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <div style={{ 
                                    padding: '16px', 
                                    border: '1px solid #f0f0f0', 
                                    borderRadius: '8px',
                                    backgroundColor: '#fafafa'
                                }}>
                                    <Radio value="enterprise">
                                        <Text strong>企业自收款</Text>
                                    </Radio>
                                    <div style={{ paddingLeft: 24, marginTop: 8, color: '#8c8c8c', fontSize: '13px' }}>
                                        需要您自行配置收款商户号，App增值服务订单收益会直接进入到您的收款账户。
                                    </div>
                                </div>
                                <div style={{ 
                                    padding: '16px', 
                                    border: '1px solid #f0f0f0', 
                                    borderRadius: '8px',
                                    backgroundColor: '#fafafa'
                                }}>
                                    <Radio value="platform">
                                        <Text strong>平台代收款</Text>
                                    </Radio>
                                    <div style={{ paddingLeft: 24, marginTop: 8, color: '#8c8c8c', fontSize: '13px' }}>
                                        App增值服务订单收益会收到平台，平台以分润的方式把订单收益按照购买分配给您。
                                    </div>
                                </div>
                            </Space>
                        </Radio.Group>
                    </Form.Item>
                </Form>
            </Drawer>
            <Drawer
                title="添加系统平台"
                width={600}
                onClose={() => setShowAddVersionDrawer(false)}
                open={showAddVersionDrawer}
                bodyStyle={{ paddingBottom: 80, padding: '24px' }}
                footer={
                    <div style={{ textAlign: 'right', padding: '10px 16px' }}>
                        <Button onClick={() => setShowAddVersionDrawer(false)} style={{ marginRight: 8 }}>取消</Button>
                        <Button onClick={handleAddVersion} type="primary">保存</Button>
                    </div>
                }
            >
                <Form 
                    form={versionForm} 
                    layout="vertical"
                    initialValues={{ region: 'mainland' }}
                >
                    <Form.Item label="选择系统平台" name="platform" rules={[{ required: true, message: '请选择系统平台' }]}>
                        <Select placeholder="请选择">
                            <Option value="Android">Android</Option>
                            <Option value="iOS">iOS</Option>
                            <Option value="微信小程序">微信小程序</Option>
                            <Option value="windows">windows</Option>
                            <Option value="macOS">macOS</Option>
                            <Option value="Linux">Linux</Option>
                            <Option value="Web/H5">Web/H5</Option>
                        </Select>
                    </Form.Item>

                    {renderPlatformInput()}

                    <Form.Item label="上架区域" name="region" rules={[{ required: true, message: '请选择上架区域' }]}>
                        <Radio.Group>
                            <Radio value="mainland">仅在中国大陆使用</Radio>
                            <Radio value="overseas">非中国大陆使用</Radio>
                            <Radio value="global">全球通用</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {renderRegionTip()}

                    <Form.Item label="收款方式">
                        <Text strong>{currentApp?.payMethod === 'platform' ? '平台代收款' : '企业自收款'}</Text>
                    </Form.Item>

                    {currentApp?.payMethod === 'enterprise' ? (
                        <Form.Item label="收款商户号" required tooltip="请选择支付通道并指定已配置的商户号">
                            <Space direction="vertical" style={{ width: '100%' }}>
                                {[
                                    { id: 'google', label: 'Google Pay', color: '#4285F4' },
                                    { id: 'apple', label: 'Apple Pay', color: '#000000' },
                                    { id: 'paypal', label: 'PayPal', color: '#003087' },
                                    { id: 'wechat', label: '微信支付', color: '#1AAD19' },
                                    { id: 'alipay', label: '支付宝', color: '#1677FF' },
                                ].map(channel => (
                                    <div key={channel.id} style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '12px',
                                        padding: '12px',
                                        border: '1px solid #f0f0f0',
                                        borderRadius: '8px',
                                        marginBottom: '8px'
                                    }}>
                                        <Form.Item name={['payConfig', channel.id, 'enabled']} valuePropName="checked" noStyle>
                                            <Checkbox>
                                                <Tag color={channel.color} style={{ margin: 0, minWidth: '80px', textAlign: 'center' }}>
                                                    {channel.label}
                                                </Tag>
                                            </Checkbox>
                                        </Form.Item>
                                        
                                        <Form.Item 
                                            noStyle
                                            shouldUpdate={(prevValues, currentValues) => 
                                                prevValues.payConfig?.[channel.id]?.enabled !== currentValues.payConfig?.[channel.id]?.enabled
                                            }
                                        >
                                            {({ getFieldValue }) => {
                                                const isEnabled = getFieldValue(['payConfig', channel.id, 'enabled']);
                                                return (
                                                    <Form.Item 
                                                        name={['payConfig', channel.id, 'accountId']} 
                                                        noStyle
                                                        rules={[{ required: isEnabled, message: '请选择商户号' }]}
                                                    >
                                                        <Select 
                                                            placeholder="请选择已配置的商户号" 
                                                            disabled={!isEnabled}
                                                            style={{ flex: 1 }}
                                                            allowClear
                                                        >
                                                            <Option value="11111">测试账号 (11111)</Option>
                                                            <Option value="22222">正式账号 (22222)</Option>
                                                        </Select>
                                                    </Form.Item>
                                                );
                                            }}
                                        </Form.Item>
                                        {!channel.id.includes('pay') && (
                                            <Button type="link" size="small" style={{ fontSize: '12px', padding: 0 }}>
                                                还没账号? 前往添加&gt;&gt;
                                            </Button>
                                        )}
                                    </div>
                                ))}
                            </Space>
                        </Form.Item>
                    ) : (
                        <Form.Item label="选择支付通道" name="payChannels" rules={[{ required: true, message: '请至少选择一个支付通道' }]}>
                            <Checkbox.Group style={{ width: '100%' }}>
                                <Row gutter={[16, 16]}>
                                    {[
                                        { label: 'Google Pay', value: 'Google Pay' },
                                        { label: 'Apple Pay', value: 'Apple Pay' },
                                        { label: 'PayPal', value: 'PayPal' },
                                        { label: '微信支付', value: '微信支付' },
                                        { label: '支付宝', value: '支付宝' },
                                    ].map(item => (
                                        <Col span={8} key={item.value}>
                                            <Checkbox value={item.value}>{item.label}</Checkbox>
                                        </Col>
                                    ))}
                                </Row>
                            </Checkbox.Group>
                        </Form.Item>
                    )}
                </Form>
            </Drawer>

            <Drawer
                title="版本详情"
                width={560}
                onClose={() => setShowVersionDetailDrawer(false)}
                open={showVersionDetailDrawer}
            >
                {currentVersion && (
                    <div style={{ padding: '0 12px' }}>
                        <Descriptions title="基本信息" column={1} bordered size="small" labelStyle={{ width: '120px', backgroundColor: '#fafafa' }}>
                            <Descriptions.Item label="当前状态">
                                {currentVersion.status === 'pending' && <Badge status="default" text="待审核" />}
                                {currentVersion.status === 'pass' && <Badge status="success" text="审核通过" />}
                                {currentVersion.status === 'reject' && <Badge status="error" text="已驳回" />}
                            </Descriptions.Item>
                            <Descriptions.Item label="系统平台">
                                <Space>
                                    {currentVersion.platform === 'iOS' ? <AppleOutlined /> : <AndroidOutlined />}
                                    {currentVersion.platform}
                                </Space>
                            </Descriptions.Item>
                            <Descriptions.Item label="上架区域">
                                {currentVersion.region === 'mainland' ? '国内' : currentVersion.region === 'overseas' ? '非大陆' : '全球'}
                            </Descriptions.Item>
                            <Descriptions.Item label="AppID">
                                <Text copyable>{currentVersion.bundleId}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="收款商户号">{currentVersion.payAccount}</Descriptions.Item>
                        </Descriptions>

                        <Divider style={{ margin: '24px 0' }} />

                        <Descriptions title="审核信息" column={1} bordered size="small" labelStyle={{ width: '120px', backgroundColor: '#fafafa' }}>
                            <Descriptions.Item label="创建时间">{currentVersion.createTime}</Descriptions.Item>
                            <Descriptions.Item label="审核时间">{currentVersion.auditTime}</Descriptions.Item>
                            <Descriptions.Item label="审核人员">{currentVersion.auditor}</Descriptions.Item>
                            {currentVersion.status === 'reject' && (
                                <Descriptions.Item label="驳回原因">
                                    <Text type="danger">{currentVersion.rejectReason}</Text>
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                        
                        <div style={{ marginTop: 24, textAlign: 'right' }}>
                            <Button onClick={() => setShowVersionDetailDrawer(false)}>关闭</Button>
                        </div>
                    </div>
                )}
            </Drawer>

            {/* 下载管理 */}
            <Drawer
                title={<Space><DownloadOutlined /><span>下载配置 - {downloadApp?.appName}</span></Space>}
                width={600}
                onClose={() => setDownloadVisible(false)}
                open={downloadVisible}
                destroyOnClose
                footer={
                    <div style={{ textAlign: 'right', padding: '10px 16px' }}>
                        <Button onClick={() => setDownloadVisible(false)} style={{ marginRight: 8 }}>取消</Button>
                        <Button type="primary" onClick={async () => {
                            try {
                                await downloadForm.validateFields();
                                message.success('下载页配置已保存');
                                setDownloadVisible(false);
                            } catch (e) { /* */ }
                        }}>保存</Button>
                    </div>
                }
            >
                <Form form={downloadForm} layout="vertical">
                    <div style={{ background: '#fff7e6', padding: '10px 14px', borderRadius: 6, marginBottom: 16, border: '1px solid #ffe58f' }}>
                        <Text style={{ fontSize: 13 }}>用户在下载App应用，系统根据手机品牌自动跳转对应渠道。对于Android操作系统，未匹配到下载渠道，平台会根据你配置的安装包引导用户安装应用。</Text>
                    </div>

                    <Text strong style={{ display: 'block', marginBottom: 12 }}>下载地址</Text>
                    <Form.Item name={['default', 'domesticAndroid']} label="国内Android（应用宝）" labelCol={{ style: { width: '100%' } }} style={{ marginBottom: 8 }}>
                        <Input placeholder="https://a.app.qq.com/o/simple.jsp?pkgname=xxx" />
                    </Form.Item>
                    <Form.Item name={['default', 'overseasAndroid']} label="海外Android（Google商店）" labelCol={{ style: { width: '100%' } }} style={{ marginBottom: 8 }}>
                        <Input placeholder="https://play.google.com/store/apps/details?id=xxx" />
                    </Form.Item>
                    <Form.Item name={['default', 'apple']} label="Apple" labelCol={{ style: { width: '100%' } }} style={{ marginBottom: 8 }}>
                        <Input placeholder="https://apps.apple.com/app/idxxxxxxx" />
                    </Form.Item>

                    <Tabs defaultActiveKey="domestic" style={{ marginTop: 8 }} items={[
                        { key: 'domestic', label: '国内Android', children: (
                            <>
                                {['小米', 'vivo', 'OPPO', '三星'].map((brand) => (
                                    <Form.Item key={brand} name={['domestic', brand]} label={brand} labelCol={{ style: { width: 70, textAlign: 'right' } }} style={{ marginBottom: 8 }}>
                                        <Input placeholder={`${brand}应用商店下载链接（选填）`} />
                                    </Form.Item>
                                ))}
                            </>
                        )},
                        { key: 'overseas', label: '海外Android', children: (
                            <div style={{ padding: '24px 0', textAlign: 'center' }}>
                                <Text type="secondary">暂无海外Android渠道配置</Text>
                            </div>
                        )},
                    ]} />

                    <Divider style={{ margin: '16px 0' }} />
                    <Text strong style={{ display: 'block', marginBottom: 12 }}>Android安装包</Text>
                    <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
                        <Form.Item name="apkDomestic" label="国内Android" style={{ flex: 1, marginBottom: 0 }}>
                            <Input placeholder="请输入国内Android安装包地址" />
                        </Form.Item>
                        <Form.Item name="apkOverseas" label="海外Android" style={{ flex: 1, marginBottom: 0 }}>
                            <Input placeholder="请输入海外Android安装包地址" />
                        </Form.Item>
                    </div>

                    <Divider style={{ margin: '16px 0' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                        <Text strong>视频指引</Text>
                        <Image width={1} height={1} src="/guide.png" preview={{ src: '/guide.png' }} id="guidePreviewImg" style={{ opacity: 0, position: 'absolute' }} />
                        <Button type="primary" ghost size="small" onClick={() => document.getElementById('guidePreviewImg')?.click()}>位置预览</Button>
                    </div>
                    <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
                        <Form.Item name="videoLinkCN" label="国内链接" style={{ flex: 1, marginBottom: 0 }}>
                            <Input placeholder="请输入国内视频链接" />
                        </Form.Item>
                        <Form.Item name="videoLinkOS" label="海外链接" style={{ flex: 1, marginBottom: 0 }}>
                            <Input placeholder="请输入海外视频链接" />
                        </Form.Item>
                    </div>

                    <Divider style={{ margin: '16px 0' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                        <Text strong>下载详情页图片</Text>
                        <Image width={1} height={1} src="/demo1.png" preview={{ src: '/demo1.png' }} id="downloadPreviewImg" style={{ opacity: 0, position: 'absolute' }} />
                        <Button type="primary" ghost size="small" onClick={() => document.getElementById('downloadPreviewImg')?.click()}>详情预览</Button>
                    </div>
                    <Form.Item name="detailImageDomestic" label="国内详情图" extra="用于下载页底部展示，建议宽度750px，支持 png、jpg 格式">
                        <Upload listType="picture-card" multiple beforeUpload={() => false}>
                            <div><PlusOutlined /><div style={{ marginTop: 8, fontSize: 12 }}>上传图片</div></div>
                        </Upload>
                    </Form.Item>
                    <Form.Item name="detailImageOverseas" label="海外详情图" extra="用于下载页底部展示，建议宽度750px，支持 png、jpg 格式">
                        <Upload listType="picture-card" multiple beforeUpload={() => false}>
                            <div><PlusOutlined /><div style={{ marginTop: 8, fontSize: 12 }}>上传图片</div></div>
                        </Upload>
                    </Form.Item>
                </Form>
            </Drawer>

            {/* 多语言配置弹窗 */}
            <Modal
                title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><GlobalOutlined style={{ color: '#1890ff' }} /><span>{i18nModal.title}</span></div>}
                open={i18nModal.open}
                onCancel={() => setI18nModal({ ...i18nModal, open: false })}
                onOk={() => { message.success('多语言配置已暂存'); setI18nModal({ ...i18nModal, open: false }); }}
                width={850}
                bodyStyle={{ maxHeight: '600px', overflowY: 'auto', padding: '12px 24px' }}
                okText="确定"
                cancelText="取消"
                zIndex={2000}
            >
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 13, color: '#666' }}>默认展示文案（中文）：</div>
                    <div style={{ padding: '10px 16px', background: '#f5f5f5', borderRadius: 6, color: '#333', fontSize: 13, border: '1px solid #eee' }}>
                        {i18nModal.originValue || '暂无文案内容'}
                    </div>
                </div>
                <Form layout="vertical">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px 24px' }}>
                        {MOCK_LANGS.map(lang => (
                            <Form.Item key={lang.key} label={
                                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                    <span style={{ fontWeight: 500 }}>{lang.label}</span>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>{lang.key}</Text>
                                </div>
                            } style={{ marginBottom: 0 }}>
                                <Input.TextArea placeholder={`请输入${lang.label}翻译内容`} rows={2} style={{ borderRadius: 6, resize: 'none' }} />
                            </Form.Item>
                        ))}
                    </div>
                </Form>
            </Modal>

            {/* 修改应用信息 */}
            <Modal title="修改应用信息" open={editAppVisible}
                onOk={async () => { try { await editAppForm.validateFields(); message.success('应用信息已更新'); setEditAppVisible(false); } catch(e){} }}
                onCancel={() => setEditAppVisible(false)} okText="保存" cancelText="取消" destroyOnClose width={520}
            >
                <Form form={editAppForm} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item label={
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <span><span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>应用名称</span>
                            <Button type="link" size="small" icon={<GlobalOutlined />}
                                onClick={() => setI18nModal({ open: true, title: '配置多语言翻译 - 应用名称', originValue: editAppForm.getFieldValue('appName') })}
                            >配置多语言 (已配置 0/{MOCK_LANGS.length})</Button>
                        </div>
                    } colon={false}>
                        <Form.Item name="appName" rules={[{ required: true, message: '请输入应用名称' }]} noStyle>
                            <Input placeholder="请输入应用名称（默认中文）" maxLength={40} showCount />
                        </Form.Item>
                    </Form.Item>
                    <Form.Item label="应用LOGO" name="appLogo" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList} extra="建议尺寸 200*200，支持 png、jpg 格式">
                        <Upload listType="picture-card" maxCount={1} beforeUpload={() => false}>
                            <div><PlusOutlined /><div style={{ marginTop: 8 }}>上传图片</div></div>
                        </Upload>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 配置支付抽屉 */}
            <Drawer
                title={`配置支付 - ${configPayRecord?.version?.platform || ''}平台`}
                width={560}
                onClose={() => { setConfigPayVisible(false); configPayForm.resetFields(); }}
                open={configPayVisible}
                destroyOnClose
                footer={
                        <div style={{ textAlign: 'right', padding: '10px 16px' }}>
                            <Button onClick={() => { setConfigPayVisible(false); configPayForm.resetFields(); }} style={{ marginRight: 8 }}>取消</Button>
                            <Button type="primary" onClick={handleSavePayConfig}>保存</Button>
                        </div>
                    }
            >
                {configPayRecord && (
                    <>
                        <div style={{ background: '#f9f9f9', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
                            <Descriptions column={1} size="small" colon={false} labelStyle={{ width: '80px', color: '#8c8c8c' }}>
                                <Descriptions.Item label="系统平台">
                                    <Space>
                                        {configPayRecord.version.platform === 'iOS' ? <AppleOutlined /> : 
                                         configPayRecord.version.platform === 'Android' ? <AndroidOutlined /> : <GlobalOutlined />}
                                        {configPayRecord.version.platform}
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label="AppID">
                                    <Text copyable>{configPayRecord.version.bundleId}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="收款方式">
                                    <Tag color={configPayRecord.app.payMethod === 'platform' ? 'blue' : 'orange'} style={{ border: 'none' }}>
                                        {configPayRecord.app.payMethod === 'platform' ? '平台代收款' : '企业自收款'}
                                    </Tag>
                                </Descriptions.Item>
                            </Descriptions>
                        </div>

                        <Form form={configPayForm} layout="vertical">
                            <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 12 }}>默认支付方式</Text>
                            {configPayRecord.app.payMethod === 'enterprise' ? (
                                <Form.Item label="收款商户号" required tooltip="请选择支付通道并指定已配置的商户号">
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        {[
                                            { id: 'Google Pay', label: 'Google Pay', color: '#4285F4' },
                                            { id: 'Apple Pay', label: 'Apple Pay', color: '#000000' },
                                            { id: 'PayPal', label: 'PayPal', color: '#003087' },
                                            { id: '微信支付', label: '微信支付', color: '#1AAD19' },
                                            { id: '支付宝', label: '支付宝', color: '#1677FF' },
                                        ].map(channel => (
                                            <div key={channel.id} style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '12px',
                                                padding: '12px',
                                                border: '1px solid #f0f0f0',
                                                borderRadius: '8px',
                                                marginBottom: '8px'
                                            }}>
                                                <Form.Item name={['payConfig', channel.id, 'enabled']} valuePropName="checked" noStyle>
                                                    <Checkbox>
                                                        <Tag color={channel.color} style={{ margin: 0, minWidth: '80px', textAlign: 'center' }}>
                                                            {channel.label}
                                                        </Tag>
                                                    </Checkbox>
                                                </Form.Item>
                                                
                                                <Form.Item 
                                                    noStyle
                                                    shouldUpdate={(prevValues, currentValues) => 
                                                        prevValues.payConfig?.[channel.id]?.enabled !== currentValues.payConfig?.[channel.id]?.enabled
                                                    }
                                                >
                                                    {({ getFieldValue }) => {
                                                        const isEnabled = getFieldValue(['payConfig', channel.id, 'enabled']);
                                                        return (
                                                            <Form.Item 
                                                                name={['payConfig', channel.id, 'accountId']} 
                                                                noStyle
                                                                rules={[{ required: isEnabled, message: '请选择商户号' }]}
                                                            >
                                                                <Select 
                                                                    placeholder="请选择已配置的商户号" 
                                                                    disabled={!isEnabled}
                                                                    style={{ flex: 1 }}
                                                                    allowClear
                                                                >
                                                                    <Option value="11111">测试账号 (11111)</Option>
                                                                    <Option value="22222">正式账号 (22222)</Option>
                                                                </Select>
                                                            </Form.Item>
                                                        );
                                                    }}
                                                </Form.Item>
                                                <Button type="link" size="small" style={{ fontSize: '12px', padding: 0 }}>
                                                    还没账号? 前往添加&gt;&gt;
                                                </Button>
                                            </div>
                                        ))}
                                    </Space>
                                </Form.Item>
                            ) : (
                                <Form.Item label="选择支付通道" name="payChannels" rules={[{ required: true, message: '请至少选择一个支付通道' }]}>
                                    <Checkbox.Group style={{ width: '100%' }}>
                                        <Row gutter={[16, 16]}>
                                            {[
                                                { label: 'Google Pay', value: 'Google Pay' },
                                                { label: 'Apple Pay', value: 'Apple Pay' },
                                                { label: 'PayPal', value: 'PayPal' },
                                                { label: '微信支付', value: '微信支付' },
                                                { label: '支付宝', value: '支付宝' },
                                            ].map(item => (
                                                <Col span={8} key={item.value}>
                                                    <Checkbox value={item.value}>{item.label}</Checkbox>
                                                </Col>
                                            ))}
                                        </Row>
                                    </Checkbox.Group>
                                </Form.Item>
                            )}
                        </Form>

                        <Divider style={{ margin: '20px 0' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <Text strong style={{ fontSize: 14 }}>区域支付覆盖（可选）</Text>
                            <Button type="link" size="small" onClick={() => { setEditingRegion(null); setRegionFormMode('custom'); setRegionFormChannels(['Google Pay']); setRegionFormCountry('日本（JP）'); setRegionModalOpen(true); }}>添加区域配置</Button>
                        </div>
                        {regionOverrides.length > 0 ? (
                            <Table size="small" pagination={false} dataSource={regionOverrides} rowKey="country"
                                columns={[
                                    { title: '国家/区域', dataIndex: 'country', width: 120, render: v => <Text strong>{v}</Text> },
                                    { title: '支付方式', dataIndex: 'channels', render: v => v.length === 0 ? <Text type="danger">无支付（用户不可购买）</Text> : <Text>{v.join(', ')}</Text> },
                                    { title: '操作', width: 120, render: (_, r) => (
                                        <Space>
                                            <Button type="link" size="small" onClick={() => { setEditingRegion(r); setRegionFormMode(r.channels.length === 0 ? 'none' : 'custom'); setRegionFormChannels(r.channels.length === 0 ? [] : r.channels); setRegionModalOpen(true); }}>编辑</Button>
                                            <Button type="link" size="small" danger onClick={() => { setRegionOverrides(prev => prev.filter(x => x.country !== r.country)); }}>删除</Button>
                                        </Space>
                                    )},
                                ]}
                            />
                        ) : <Text type="secondary" style={{ fontSize: 12 }}>暂无区域配置，所有区域使用默认支付方式</Text>}
                    </>
                )}
            </Drawer>

            {/* 区域支付配置 Modal */}
            <Modal title={editingRegion ? '编辑区域支付配置' : '添加区域支付配置'} open={regionModalOpen}
                onCancel={() => setRegionModalOpen(false)} 
                onOk={() => {
                    const channels = regionFormMode === 'none' ? [] : regionFormChannels;
                    if (editingRegion) {
                        setRegionOverrides(prev => prev.map(r => r.country === editingRegion.country ? { ...r, channels } : r));
                    } else {
                        if (regionOverrides.find(r => r.country === regionFormCountry)) { message.warning('该国家已有区域配置'); return; }
                        setRegionOverrides(prev => [...prev, { country: regionFormCountry, channels }]);
                    }
                    setRegionModalOpen(false);
                }} okText="保存" cancelText="取消" destroyOnClose width={420}>
                <div style={{ marginTop: 16 }}>
                    <Text strong>国家/区域：</Text>
                    <Select value={regionFormCountry} onChange={setRegionFormCountry} placeholder="选择国家" style={{ width: '100%', marginTop: 8, marginBottom: 16 }}>
                        {['日本（JP）', '巴西（BR）', '印度（IN）', '韩国（KR）'].filter(c => !regionOverrides.find(r => r.country === c) || editingRegion?.country === c).map(c => (
                            <Option key={c} value={c}>{c}</Option>
                        ))}
                    </Select>
                    <Text strong>支付方式：</Text>
                    <Radio.Group value={regionFormMode} onChange={e => { setRegionFormMode(e.target.value); if (e.target.value === 'none') setRegionFormChannels([]); }}
                        style={{ marginTop: 8, display: 'block' }}>
                        <Space direction="vertical">
                            <Radio value="custom">自定义支付方式</Radio>
                            <Radio value="none">不配置支付（该国家用户不可购买）</Radio>
                        </Space>
                    </Radio.Group>
                    {regionFormMode === 'custom' && (
                        <div style={{ marginTop: 12 }}>
                            {configPayRecord?.app?.payMethod === 'enterprise' ? (
                                allChannels.map(c => (
                                    <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, border: '1px solid #f0f0f0', borderRadius: 8, marginBottom: 8 }}>
                                        <Checkbox value={c} checked={regionFormChannels.includes(c)} onChange={e => {
                                            setRegionFormChannels(e.target.checked ? [...regionFormChannels, c] : regionFormChannels.filter(x => x !== c));
                                        }}>
                                            <Tag style={{ margin: 0, minWidth: 80, textAlign: 'center' }}>{c}</Tag>
                                        </Checkbox>
                                        <Select placeholder="选择商户号" disabled={!regionFormChannels.includes(c)} style={{ flex: 1 }}>
                                            <Option value="11111">测试账号 (11111)</Option>
                                            <Option value="22222">正式账号 (22222)</Option>
                                        </Select>
                                    </div>
                                ))
                            ) : (
                                <Checkbox.Group value={regionFormChannels} onChange={setRegionFormChannels} style={{ width: '100%' }}>
                                    <Row gutter={[8, 8]}>
                                        {allChannels.map(c => <Col span={12} key={c}><Checkbox value={c}>{c}</Checkbox></Col>)}
                                    </Row>
                                </Checkbox.Group>
                            )}
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default AppManage;
